import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { getDataNetworks, getDataPlans } from '../api/apiServices';

export default function TopDataOffersSection({
  title = "Top Data Offers",
  maxItems = 6,
  onOfferPress,
  containerStyle = "",
  cardStyle = "",
}) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAllTopOffers = async () => {
    setLoading(true);
    setError(null);

    try {
      const networksRes = await getDataNetworks();
      const networks = networksRes?.data?.networks || [];

      let allOffers = [];

      for (const net of networks) {
        try {
          const plansRes = await getDataPlans(net.identifier);
          let plansArray = [];

          if (plansRes?.data?.plans?.length) plansArray = plansRes.data.plans;
          else if (Array.isArray(plansRes?.data)) plansArray = plansRes.data;
          else if (plansRes?.data?.data?.length) plansArray = plansRes.data.data;

          const formatted = plansArray.map((plan) => {
            const label = (
              plan.label ||
              plan.data ||
              plan.size ||
              plan.plan_summary ||
              ''
            ).replace(/\|/g, ' • ');

            const amount = Number(
              plan.amount ??
                plan.price ??
                plan.regular_price ??
                plan.agent_price ??
                plan.dealer_price ??
                0
            );

            const planCode = plan.plan_code || plan.package_code || '';

            // ?? not || — an explicit `false` must survive, not get
            // coerced into the fallback the way `false || true` would.
            const available = plan.available ?? true;

            return {
              data: label || 'N/A',
              size: label || 'N/A',
              amount,
              price: amount,
              validity: plan.validity || label || 'N/A',
              plan_code: planCode,
              network: net.name.split('(')[0].trim(),
              networkIdentifier: net.identifier,
              label,
              available,
            };
          });

          allOffers = [...allOffers, ...formatted];
        } catch (err) {
          console.warn(`Failed to load plans for ${net.name}`, err);
        }
      }

      // Filter out unavailable plans alongside the existing price filter
      const sorted = allOffers
        .filter((offer) => offer.price > 0 && offer.available !== false)
        .sort((a, b) => a.price - b.price)
        .slice(0, maxItems);

      setOffers(sorted);
    } catch (err) {
      console.error('Failed to load top offers:', err);
      setError('Unable to load top offers at the moment');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllTopOffers();
  }, []);

  if (loading) {
    return (
      <div className={`py-10 flex items-center justify-center ${containerStyle}`}>
        <div className="w-8 h-8 border-4 border-[#3866A3] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`py-8 px-4 text-center ${containerStyle}`}>
        <p className="text-red-600 font-medium">{error}</p>
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <div className={`py-8 px-4 text-center ${containerStyle}`}>
        <p className="text-gray-500">No top offers available right now</p>
      </div>
    );
  }

  return (
    <section className={`space-y-5 ${containerStyle}`}>
      <h2 className="text-[20px] font-bold text-[#3866A3] px-1">{title}</h2>

      <div className="px-2">
        <div className="grid grid-cols-3 gap-3">
          {offers.map((offer, index) => (
            <motion.div
              key={`${offer.plan_code}-${index}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: index * 0.07 }}
              className={`relative bg-[#3866A3] rounded-2xl p-3 overflow-hidden cursor-pointer 
                         active:scale-[0.97] transition-all hover:shadow-lg ${cardStyle}`}
              onClick={() => onOfferPress?.(offer)}
            >
              <div className="absolute -top-1 -left-1 w-8 h-8 bg-[#FF383C] rounded-br-2xl flex items-center justify-center">
                <span className="text-xl drop-shadow">🔥</span>
              </div>

              <div className="flex flex-col items-center mt-5 text-center">
                <p className="text-white font-bold text-xs tracking-tight leading-none">
                  {offer.data}
                </p>

                <p className="text-base font-bold text-white mt-1.5 leading-none">
                  ₦{offer.price.toLocaleString()}
                </p>

                <div className="mt-3 px-3 py-0.5 bg-white/20 text-white text-[10px] rounded-full">
                  {offer.network}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}